import React, { useEffect, useMemo } from "react";
import { getCurrentEnvironmentId } from "ee/selectors/environmentSelectors";
import { getIsViewMode } from "selectors/editorSelectors";
import { ContainerComponent } from "widgets/wds/Container";
import { Elevations } from "widgets/wds/constants";
import { AIChat } from "../component";
import { getSegmentState } from "selectors/analyticsSelectors";
import { useAIChatCoordinator, useMessages } from "../hooks";
import { useSelector } from "react-redux";
import { getActionByBaseId } from "ee/selectors/entitiesSelector";
import { getLoggers } from "../utils/loggingUtils";

export interface AIChatWidgetProps {
  isWidgetSelected: boolean;
  widgetId: string;
  initialAssistantMessage: string;
  initialAssistantSuggestions: Array<{ label: string; value: string }>;
  assistantName: string;
  promptInputPlaceholder: string;
  chatHeightSize: "MEDIUM" | "LARGE" | "FIT_PAGE";
  queryRun: string;
}

export function AIChatWidget(props: AIChatWidgetProps) {
  const {
    assistantName,
    chatHeightSize,
    initialAssistantMessage,
    initialAssistantSuggestions,
    isWidgetSelected,
    promptInputPlaceholder,
    queryRun,
    widgetId,
  } = props;

  const query = useSelector((state) => getActionByBaseId(state, queryRun));
  const activeEnv = useSelector(getCurrentEnvironmentId);
  const viewMode = useSelector(getIsViewMode);
  const segmentState = useSelector(getSegmentState);

  // Initialize the coordinator state with props
  // The state id keyed by widgetId + queryId for uniqueness
  // As widgetId is always the same, but queryId is new for each app
  const { startOperation, state, stopOperation, updateState } =
    useAIChatCoordinator(widgetId + query?.id);

  const { logError, logEvent } = useMemo(
    () =>
      getLoggers({
        threadId: state.threadId,
        widgetId,
      }),
    [state.threadId, widgetId],
  );

  // Initialize messages handling
  const {
    approveToolCall,
    cancelRun,
    createNewThread,
    getMessages,
    getThreads,
    handlePromptChange,
    messages,
    prompt,
    rejectToolCall,
    sendMessage,
  } = useMessages({
    state,
    updateState,
    startOperation,
    stopOperation,
    widgetId,
    queryId: query?.id || "",
    activeEnv,
    viewMode,
    initialAssistantMessage,
    initialAssistantSuggestions,
    logError,
    logEvent,
  });

  // Initialize the component
  useEffect(
    function initializeComponent() {
      // We are using segment to generate a unique id for the anonymous user, so segment should be initialized first
      // before we can get threads as it needs the user id
      if (segmentState !== "INIT_SUCCESS") return;

      // When component is mounted after switch from datasource or to view mode,
      // the widget remains unselected, allowing us to request threads or messages
      if (state.threadId && !state.isThreadLoading && messages.length === 0) {
        getMessages(state.threadId);
      }

      if (
        !state.threadId &&
        !state.isThreadLoading &&
        messages.length === 0 &&
        query
      ) {
        getThreads();
      }
    },
    [
      state.threadId,
      isWidgetSelected,
      query,
      updateState,
      getThreads,
      segmentState,
      getMessages,
      state.isThreadLoading,
      messages.length,
    ],
  );

  return (
    <ContainerComponent
      elevatedBackground
      elevation={Elevations.CARD_ELEVATION}
      noPadding
      widgetId={widgetId}
    >
      <AIChat
        chatTitle={assistantName}
        isCancelling={state.isCancelling}
        isProcessing={state.isProcessing}
        isQueryLoading={false}
        isThreadLoading={state.isThreadLoading}
        isWaitingForResponse={state.isWaitingForResponse}
        onApplyAssistantSuggestion={handlePromptChange}
        onDeleteThread={createNewThread}
        onPromptChange={handlePromptChange}
        onStop={cancelRun}
        onSubmit={sendMessage}
        onToolCallApprove={approveToolCall}
        onToolCallReject={rejectToolCall}
        prompt={prompt}
        promptInputPlaceholder={promptInputPlaceholder}
        queryId={queryRun}
        size={chatHeightSize}
        thread={messages}
        threadId={state.threadId}
      />
    </ContainerComponent>
  );
}
